/**
 * Pieces every desk variant draws the same way: the desk's frame and
 * headings, what is being read now, a record as one dense row, a lookup that
 * returned nothing or failed, and a disclosure. Records open in a new tab
 * (`RecordLink`), so the call keeps running.
 *
 * @module components/apps/probe/desk/DeskParts
 */
import {
  ArrowUpRightIcon,
  CheckIcon,
  ChevronRightIcon,
  LoaderCircleIcon,
  SearchXIcon,
  TriangleAlertIcon,
} from "lucide-react";
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { cn } from "~/lib/utils";
import { Badge } from "../../../ui/badge";
import { DESK_LABEL_CLASS, FILE_SECTIONS, KIND_ICON, LINKED_CLASS, RecordLink } from "../ProbeDesk";
import type { ProbeDeskFile, ProbeDeskReading, ProbeDeskRow } from "../ProbeDesk.logic";
import { SeverityGlyph } from "../ProbeParts";
import type { DeskCheck, DeskKind, DeskRecord, DeskRecordMore } from "./deskRecords";

export const KIND_LABEL: Readonly<
  Record<DeskKind, { readonly one: string; readonly many: string }>
> = {
  finding: { one: "Finding", many: "Findings" },
  policy: { one: "Policy", many: "Policies" },
  update: { one: "Regulator change", many: "Regulator changes" },
  passage: { one: "Authority passage", many: "Authority passages" },
  web: { one: "Web page", many: "Web pages" },
  rehearsal: { one: "Past rehearsal", many: "Past rehearsals" },
};

/** The desk's scrolling column and its title, as the stage and the sheet draw it. */
export function DeskFrame({
  headingId,
  children,
  variant,
}: {
  readonly headingId?: string | undefined;
  readonly children: ReactNode;
  readonly variant: string;
}) {
  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pt-4 pb-6 [&>*]:shrink-0"
      data-probe-desk=""
      data-probe-desk-variant={variant}
    >
      <h2 id={headingId} className="sr-only">
        The examiner&rsquo;s file
      </h2>
      {children}
    </div>
  );
}

/** A part of the desk: a heading a reader can scan for, with its count, over a rule. */
export function DeskSection({
  title,
  count,
  first = false,
  children,
  ...rest
}: {
  readonly title: ReactNode;
  readonly count?: ReactNode;
  /** The first section draws no rule above it. */
  readonly first?: boolean;
  readonly children: ReactNode;
} & Record<`data-${string}`, string>) {
  const id = useId();
  return (
    <section
      aria-labelledby={id}
      className={cn("flex flex-col gap-3", !first && "mt-5 border-t border-border/70 pt-4")}
      {...rest}
    >
      <h3 id={id} className="flex items-baseline gap-2 text-[13px] font-semibold text-foreground">
        <span className="min-w-0 flex-1">{title}</span>
        {count !== undefined ? (
          <span className={cn("shrink-0 text-xs font-normal tabular-nums", DESK_LABEL_CLASS)}>
            {count}
          </span>
        ) : null}
      </h3>
      {children}
    </section>
  );
}

/** A kind's label inside a section: its icon, its name and how many. */
export function KindHeading({
  kind,
  count,
  note,
}: {
  readonly kind: DeskKind;
  readonly count: number;
  readonly note?: ReactNode;
}) {
  const Icon = KIND_ICON[kind];
  return (
    <h4 className={cn("flex items-center gap-1.5 text-xs font-medium", DESK_LABEL_CLASS)}>
      <Icon aria-hidden className="size-3.5 shrink-0" />
      <span>{count === 1 ? KIND_LABEL[kind].one : KIND_LABEL[kind].many}</span>
      <span className="tabular-nums">{count}</span>
      {note !== undefined ? <span className="ms-auto font-normal">{note}</span> : null}
    </h4>
  );
}

/** What the examiner is looking up this moment. */
export function ReadingNow({
  reading,
  label = "Reading now",
}: {
  readonly reading: ReadonlyArray<ProbeDeskReading>;
  readonly label?: string;
}) {
  const id = useId();
  if (reading.length === 0) return null;
  return (
    <section
      aria-labelledby={id}
      className="rounded-lg border border-primary/25 bg-card px-3 py-2.5"
      data-probe-desk-reading=""
    >
      <h3 id={id} className="flex items-center gap-1.5 text-xs font-medium text-foreground">
        <LoaderCircleIcon aria-hidden className="size-3.5 text-primary motion-safe:animate-spin" />
        {label}
      </h3>
      <ul className="mt-1.5 flex flex-col gap-1.5">
        {reading.map((one) => (
          <li key={one.callId} className="ps-5">
            <p className="text-[13px] leading-snug font-medium">{one.title}</p>
            {one.detail !== null ? (
              <p className={cn("text-xs", DESK_LABEL_CLASS)}>{one.detail}</p>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** A bordered list of rows, ruled between them. */
export function Rows({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <ul
      className={cn(
        "divide-y divide-border/60 overflow-hidden rounded-lg border border-border/70 bg-card",
        className,
      )}
    >
      {children}
    </ul>
  );
}

/**
 * A record as one row: a finding leads with its severity, anything else with
 * its kind's icon when `icon`; then its title, one quiet line, and a note.
 */
export function RecordRow({
  record,
  orgSegment,
  icon = false,
  note,
  clamp = 2,
  muted = false,
  bare = false,
}: {
  readonly record: DeskRecord;
  readonly orgSegment: string;
  readonly icon?: boolean;
  readonly note?: ReactNode;
  readonly clamp?: 1 | 2 | 3;
  /** Seen in a list rather than opened: the title is lighter. */
  readonly muted?: boolean;
  /** The title alone, without the record's detail. */
  readonly bare?: boolean;
}) {
  const Icon = KIND_ICON[record.kind];
  const lead =
    record.kind === "finding" && record.severity !== null ? (
      <SeverityGlyph severity={record.severity} className="mt-0.5" />
    ) : icon ? (
      <Icon aria-hidden className={cn("mt-0.5 size-3.5 shrink-0", DESK_LABEL_CLASS)} />
    ) : null;
  return (
    <RecordLink
      link={record.link}
      orgSegment={orgSegment}
      className="flex items-start gap-2.5 px-3 py-2"
    >
      {lead}
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block text-[13px] leading-snug",
            muted ? "text-foreground/85" : "font-medium text-foreground",
          )}
        >
          {record.title}
        </span>
        {record.detail !== null && !bare ? (
          <span
            className={cn(
              "mt-0.5 block text-xs leading-snug",
              DESK_LABEL_CLASS,
              clamp === 1 ? "line-clamp-1" : clamp === 2 ? "line-clamp-2" : "line-clamp-3",
            )}
          >
            {record.detail}
          </span>
        ) : null}
        {note !== undefined && note !== null ? (
          <span className={cn("mt-1 block text-[11px]", DESK_LABEL_CLASS)}>{note}</span>
        ) : null}
      </span>
    </RecordLink>
  );
}

const OPEN_LINK: Readonly<Record<NonNullable<DeskRecord["link"]>["kind"], string>> = {
  finding: "Open finding",
  policy: "Open policy",
  url: "Open page",
};

/** How long a row takes to open or close, and how long a chip-opened row's flash lasts (`probe-row-flash`). */
const ROW_EXPAND_MS = 200;
const ROW_FLASH_MS = 1_200;

const stillMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? true;

interface OpenRows {
  readonly open: string | null;
  readonly setOpen: (key: string | null) => void;
  /** A record asked for from outside the desk (a chip under a line): opened, then scrolled to once drawn. */
  readonly shown: { readonly key: string; readonly at: number } | null;
  readonly show: (key: string) => void;
  /** A row has brought itself into view for `shown`; a sheet opened later starts at its top. */
  readonly settle: (at: number) => void;
  /** Each row's elements, by its record's key: the column's and the sheet's may both be drawn. */
  readonly elements: Map<string, Set<HTMLElement>>;
}

const OpenRowContext = createContext<OpenRows | null>(null);

export const useOpenRows = (): OpenRows | null => useContext(OpenRowContext);

/**
 * Rows that open one at a time, shared by everything inside: the desk, and
 * on a page the chips under the lines that open its rows. A click anywhere
 * outside the open row, or Escape, closes it; Escape then goes no further,
 * so a sheet around the desk stays open. `onShow` runs when a chip asks for
 * a row, for a page whose desk is behind a sheet.
 */
export function OpenRowsProvider({
  children,
  onShow,
}: {
  readonly children: ReactNode;
  readonly onShow?: (key: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [shown, setShown] = useState<OpenRows["shown"]>(null);
  const elements = useRef(new Map<string, Set<HTMLElement>>()).current;
  const onShowRef = useRef(onShow);
  onShowRef.current = onShow;
  const show = useCallback((key: string) => {
    setOpen(key);
    setShown({ key, at: Date.now() });
    onShowRef.current?.(key);
  }, []);
  const settle = useCallback(
    (at: number) => setShown((was) => (was !== null && was.at === at ? null : was)),
    [],
  );
  useEffect(() => {
    if (open === null) return;
    const rowsOf = () => [...(elements.get(open) ?? [])];
    const away = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Node) || !rowsOf().some((row) => row.contains(target))) setOpen(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // A row left open in a desk since hidden (the column, below its width) does not take the key.
      if (rowsOf().some((row) => row.checkVisibility?.() !== false)) event.stopPropagation();
      setOpen(null);
    };
    // On click, not pointerdown: closing collapses the row, and rows below it would slide out
    // from under a press still held, so its release would land on another row.
    document.addEventListener("click", away, true);
    window.addEventListener("keydown", escape, true);
    return () => {
      document.removeEventListener("click", away, true);
      window.removeEventListener("keydown", escape, true);
    };
  }, [open, elements]);
  const value = useMemo(
    () => ({ open, setOpen, shown, show, settle, elements }),
    [open, shown, show, settle, elements],
  );
  return <OpenRowContext.Provider value={value}>{children}</OpenRowContext.Provider>;
}

/** The desk's rows, sharing the page's open row when the page holds one. */
export function OpenOneRow({ children }: { readonly children: ReactNode }) {
  const outer = useContext(OpenRowContext);
  return outer !== null ? children : <OpenRowsProvider>{children}</OpenRowsProvider>;
}

/**
 * A record as its title, a finding led by its severity, that opens in place
 * to what matters for its kind and one link to its page in a new tab.
 */
export function ExpandingRecordRow({
  record,
  orgSegment,
}: {
  readonly record: DeskRecord;
  readonly orgSegment: string;
}) {
  const rows = useContext(OpenRowContext);
  const id = useId();
  const toggle = useRef<HTMLButtonElement>(null);
  const row = useRef<HTMLDivElement | null>(null);
  const [flashAt, setFlashAt] = useState<number | null>(null);
  const shownAt = rows?.shown?.key === record.key ? rows.shown.at : null;
  const open = rows?.open === record.key;
  useEffect(() => {
    const button = toggle.current;
    if (shownAt === null || button === null || button.checkVisibility?.() === false) return;
    button.focus({ preventScroll: true });
    button.parentElement?.scrollIntoView({
      block: "nearest",
      behavior: stillMotion() ? "auto" : "smooth",
    });
    setFlashAt(shownAt);
    rows?.settle(shownAt);
  }, [shownAt, rows]);
  useEffect(() => {
    if (flashAt === null) return;
    // A second request while the first still flashes starts the flash over.
    const style = row.current?.style;
    if (style !== undefined) {
      style.animation = "none";
      void row.current?.offsetWidth;
      style.removeProperty("animation");
    }
    const timer = setTimeout(() => setFlashAt(null), ROW_FLASH_MS);
    return () => clearTimeout(timer);
  }, [flashAt]);
  useEffect(() => {
    // Once open, the whole row in view, its opened part included.
    const node = row.current;
    if (!open || node === null || node.checkVisibility?.() === false) return;
    const still = stillMotion();
    const timer = setTimeout(
      () => node.scrollIntoView?.({ block: "nearest", behavior: still ? "auto" : "smooth" }),
      still ? 0 : ROW_EXPAND_MS,
    );
    return () => clearTimeout(timer);
  }, [open]);
  if (rows === null) throw new Error("ExpandingRecordRow draws inside OpenOneRow.");
  const { text, status, fields } = record.more;
  return (
    <div
      ref={(node) => {
        row.current = node;
        if (node === null) return;
        const drawn = rows.elements.get(record.key) ?? new Set<HTMLElement>();
        drawn.add(node);
        rows.elements.set(record.key, drawn);
        return () => {
          drawn.delete(node);
          if (drawn.size === 0) rows.elements.delete(record.key);
        };
      }}
      className={cn("transition-colors", open && "bg-accent/50")}
      data-probe-desk-row={record.key}
      data-open={open ? "" : undefined}
      data-probe-flash={flashAt === null ? undefined : ""}
    >
      <button
        ref={toggle}
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => rows.setOpen(open ? null : record.key)}
        className={cn(
          "flex w-full cursor-pointer items-start gap-2.5 px-3 py-2 text-start",
          LINKED_CLASS,
          "focus-visible:ring-inset",
        )}
      >
        {record.kind === "finding" && record.severity !== null ? (
          <SeverityGlyph severity={record.severity} className="mt-0.5" />
        ) : null}
        <span className="min-w-0 flex-1 text-[13px] leading-snug font-medium text-foreground">
          {record.title}
        </span>
      </button>
      <div
        id={id}
        inert={!open}
        aria-hidden={!open}
        className={cn(
          "grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
        data-probe-desk-more=""
      >
        <div className="min-h-0 overflow-hidden">
          <div
            className={cn(
              "flex flex-col gap-2 pb-3 pe-3 text-xs leading-relaxed",
              record.kind === "finding" && record.severity !== null ? "ps-9.5" : "ps-3",
            )}
          >
            {text !== null ? <p className="line-clamp-5 text-foreground/85">{text}</p> : null}
            <RecordFacts status={status} fields={fields} />
            {record.link !== null ? (
              <RecordLink
                link={record.link}
                orgSegment={orgSegment}
                className="mt-0.5 inline-flex h-6 items-center gap-1 self-start rounded-md border border-border/70 bg-background px-2 text-xs font-medium text-foreground shadow-xs/5 hover:bg-accent/60"
              >
                {OPEN_LINK[record.link.kind]}
                <ArrowUpRightIcon aria-hidden className="size-3 opacity-70" />
              </RecordLink>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** An opened row's facts: label and value in two columns, the status as a badge. */
function RecordFacts({
  status,
  fields,
}: {
  readonly status: DeskRecordMore["status"];
  readonly fields: DeskRecordMore["fields"];
}) {
  if (status === null && fields.length === 0) return null;
  return (
    <dl className="grid grid-cols-[minmax(4.5rem,auto)_minmax(0,1fr)] items-baseline gap-x-3 gap-y-1">
      {status !== null ? (
        <>
          <dt className={DESK_LABEL_CLASS}>Status</dt>
          <dd>
            <Badge variant="outline" size="sm" className="font-normal">
              {status}
            </Badge>
          </dd>
        </>
      ) : null}
      {fields.map((field) => (
        <Fragment key={field.label}>
          <dt className={DESK_LABEL_CLASS}>{field.label}</dt>
          <dd className="min-w-0 break-words text-foreground">{field.value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

/** Whether a record opened was also seen in a list: one record, read twice. */
export const readHow = (record: DeskRecord): string | null =>
  filedNote(record) ??
  (record.opened && record.listedIn.length > 0 ? `Also in ${record.listedIn.join(" and ")}` : null);

/** A web page that is a regulator change in the file says which. */
export const filedNote = (record: DeskRecord): string | null =>
  record.filedAs === null ? null : "On file as a regulator change";

/** A lookup that failed, with the server's reason. */
export function FailedLine({
  title,
  reason,
  className,
}: {
  readonly title: string;
  readonly reason: string | null;
  readonly className?: string;
}) {
  return (
    <div className={cn("flex items-start gap-2", className)} data-probe-desk-failed="">
      <TriangleAlertIcon aria-hidden className="mt-0.5 size-3.5 shrink-0 text-warning" />
      <div className="min-w-0 flex-1">
        <p className={cn("text-xs", DESK_LABEL_CLASS)}>Couldn&rsquo;t read</p>
        <p className="text-[13px] leading-snug font-medium">{title}</p>
        {reason !== null ? (
          <p className={cn("mt-0.5 line-clamp-3 text-xs", DESK_LABEL_CLASS)}>{reason}</p>
        ) : null}
      </div>
    </div>
  );
}

/** A lookup that came back with no record: found nothing, or checked something that has none. */
export function NothingLine({
  title,
  query,
  checked,
  className,
}: {
  readonly title: string;
  readonly query: string | null;
  readonly checked: boolean;
  readonly className?: string;
}) {
  const Icon = checked ? CheckIcon : SearchXIcon;
  return (
    <div
      className={cn("flex items-start gap-2 text-xs", DESK_LABEL_CLASS, className)}
      data-probe-desk-nothing=""
    >
      <Icon aria-hidden className="mt-px size-3.5 shrink-0" />
      <p className="min-w-0 flex-1 leading-snug">
        {title}
        {query !== null ? <span className="block line-clamp-2">{query}</span> : null}
      </p>
    </div>
  );
}

/**
 * Lookups that returned no record: each failure says so with its reason;
 * searches that found nothing and checks of what holds no record follow
 * under their own label.
 */
export function DeskChecks({ checks }: { readonly checks: ReadonlyArray<DeskCheck> }) {
  const failed = checks.filter((check) => check.kind === "failed");
  const nothing = checks.filter((check) => check.kind === "nothing");
  return (
    <>
      {failed.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {failed.map((check) => (
            <li key={check.key}>
              <FailedLine title={check.title} reason={check.reason} />
            </li>
          ))}
        </ul>
      ) : null}
      {nothing.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <h4 className={cn("text-xs font-medium", DESK_LABEL_CLASS)}>Searches and checks</h4>
          <ul className="flex flex-col gap-1.5">
            {nothing.map((check) => (
              <li key={check.key}>
                <NothingLine title={check.title} query={check.query} checked={check.checked} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}

/** A row that opens to more rows. */
export function Disclosure({
  summary,
  children,
  className,
}: {
  readonly summary: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-controls={id}
        className={cn(
          "flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-start text-xs",
          DESK_LABEL_CLASS,
          LINKED_CLASS,
          className,
        )}
      >
        <ChevronRightIcon
          aria-hidden
          className={cn("size-3.5 shrink-0 motion-safe:transition-transform", open && "rotate-90")}
        />
        {summary}
      </button>
      <div id={id} hidden={!open}>
        {children}
      </div>
    </>
  );
}

/** Rows past a preview, behind "Show all". */
export function Preview<T>({
  items,
  shown,
  render,
  more,
}: {
  readonly items: ReadonlyArray<T>;
  readonly shown: number;
  readonly render: (item: T) => ReactNode;
  readonly more: (hidden: number) => string;
}) {
  const [all, setAll] = useState(false);
  const visible = all ? items : items.slice(0, shown);
  return (
    <>
      {visible.map(render)}
      {items.length > shown ? (
        <li>
          <button
            type="button"
            onClick={() => setAll((was) => !was)}
            aria-expanded={all}
            className={cn(
              "w-full cursor-pointer px-3 py-1.5 text-start text-xs",
              DESK_LABEL_CLASS,
              LINKED_CLASS,
            )}
          >
            {all ? "Show fewer" : more(items.length - shown)}
          </button>
        </li>
      ) : null}
    </>
  );
}

/** A note where a part of the desk has nothing to show. */
export function Quiet({ children }: { readonly children: ReactNode }) {
  return (
    <p
      className={cn(
        "rounded-lg border border-dashed border-border/70 px-3 py-2.5 text-xs",
        DESK_LABEL_CLASS,
      )}
    >
      {children}
    </p>
  );
}

const FILE_PREVIEW = 8;

/** What is left in the file, a section a row, each opening to its items. */
export function FileLeft({
  file,
  orgSegment,
}: {
  readonly file: ProbeDeskFile;
  readonly orgSegment: string;
}) {
  if (file.empty) return <Quiet>The file is empty.</Quiet>;
  if (file.sections.length === 0) return <Quiet>All read.</Quiet>;
  return (
    <Rows>
      {file.sections.map(({ section, count, rows, unlisted }) => {
        const { label, icon: Icon } = FILE_SECTIONS[section];
        return (
          <li key={section} data-probe-desk-file={section}>
            <Disclosure
              className="text-[13px] text-foreground dark:text-foreground"
              summary={
                <>
                  <Icon aria-hidden className={cn("size-3.5 shrink-0", DESK_LABEL_CLASS)} />
                  <span className="min-w-0 flex-1">{label}</span>
                  <span className={cn("shrink-0 text-xs tabular-nums", DESK_LABEL_CLASS)}>
                    {count}
                  </span>
                </>
              }
            >
              <ul className="divide-y divide-border/50 border-t border-border/50">
                <Preview
                  items={rows}
                  shown={FILE_PREVIEW}
                  more={(hidden) => `Show ${hidden} more`}
                  render={(row) => (
                    <li key={row.key}>
                      <FileRow
                        row={row}
                        severity={section === "findings"}
                        orgSegment={orgSegment}
                      />
                    </li>
                  )}
                />
                {unlisted > 0 ? (
                  <li className={cn("px-3 py-1.5 text-xs", DESK_LABEL_CLASS)}>
                    And {unlisted} more the examiner looks up during the call
                  </li>
                ) : null}
              </ul>
            </Disclosure>
          </li>
        );
      })}
    </Rows>
  );
}

function FileRow({
  row,
  severity,
  orgSegment,
}: {
  readonly row: ProbeDeskRow;
  readonly severity: boolean;
  readonly orgSegment: string;
}) {
  return (
    <RecordLink
      link={row.link}
      orgSegment={orgSegment}
      className="flex items-baseline gap-2 py-1.5 ps-8.5 pe-3"
    >
      {severity && row.note !== null ? (
        <SeverityGlyph severity={row.note} className="self-center" />
      ) : null}
      <span className="min-w-0 flex-1 truncate text-xs">{row.title}</span>
      {!severity && row.note !== null ? (
        <span className={cn("shrink-0 text-[11px] tabular-nums", DESK_LABEL_CLASS)}>
          {row.note}
        </span>
      ) : null}
    </RecordLink>
  );
}
